import type { MatterType } from '@common/enums';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Typography,
    useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import EditableViewComponentLeftAligned from '@/components/ReadOnlyComponent/EditableViewComponentLeftAligned';
import { dateStringRegex, ordinalNumbers, uidRegex } from '@/constants';
import { getFileDetails } from '@/services/FileService';
import { getQuoteFormResponse } from '@/services/ProspectService';
import { isAxiosNotFoundError } from '@/utils/queryError';
import { errorSnackbar } from '@/utils/snackbar';
import ProspectDetailsEmptyPage from './ProspectDetailsEmptyPage';
import ProspectDetailsErrorPage from './ProspectDetailsErrorPage';
import ProspectQuoteFromResponseSkeleton from './ProspectQuoteFromResponseSkeleton';

interface QuoteFormField {
    Name: string;
    Title: string;
    Value: string;
    DisplayOrder: number;
    _fileId?: string;
}

interface Props {
    prospectId?: string;
    matterType: MatterType;
}

function isUUID(value: string | undefined) {
    return value
        ?.split('|')
        ?.filter(Boolean)
        ?.every((part) =>
            part
                ?.split(',')
                ?.filter(Boolean)
                ?.every((subPart) => uidRegex.test(subPart.trim())),
        );
}

async function resolveUploadFields(data: QuoteFormField[]): Promise<QuoteFormField[]> {
    const dataWithFields: QuoteFormField[] = [];

    for (const field of data) {
        if (isUUID(field.Value) && field.Value.includes('|')) {
            const idGroups = field.Value.split('|');
            const fieldIds: string[][] = idGroups.map((group) => group.split(','));

            for (let i = 0; i < fieldIds.length; i++) {
                const apiCalls = fieldIds[i]?.filter((id) => !!id).map((id) => getFileDetails(id));

                const apiResp = await Promise.allSettled(apiCalls);
                apiResp.forEach((promise) => {
                    if (promise.status === 'fulfilled') {
                        const file = promise.value.data;

                        dataWithFields.push({
                            ...field,
                            Value: file.fileName,
                            _fileId: file.fileId,
                        });
                    }
                });
            }
        } else if (uidRegex.test(field.Value?.split(',')[0])) {
            const fieldIds: string[] = field.Value?.split(',');
            const apiCalls = fieldIds?.filter((id) => !!id).map((id) => getFileDetails(id));

            const apiResp = await Promise.allSettled(apiCalls);
            apiResp.forEach((promise) => {
                if (promise.status === 'fulfilled') {
                    const file = promise.value.data;

                    dataWithFields.push({
                        ...field,
                        Value: file.fileName,
                        _fileId: file.fileId,
                    });
                }
            });
        } else {
            dataWithFields.push({ ...field });
        }
    }

    return dataWithFields;
}

function getContactInfoHeader(matterType: MatterType): string {
    switch (matterType) {
        case 'PrivateLender':
            return 'Lender(s) info';
        case 'Purchase':
            return 'Purchaser(s) info';
        case 'Sale':
            return 'Seller(s) info';
        case 'Refinance':
            return 'Borrower(s) info';
        default:
            return 'Contact info';
    }
}

async function handleDownload(fileUrl: string, fileName: string) {
    if (!fileUrl || !fileName) {
        errorSnackbar('File does not exist');
        return;
    }

    try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
        URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error downloading the file:', error);
    }
}

function formatFieldValue(title: string, value: string | undefined) {
    if (
        ['Enter your purchase price', 'Enter your sale price', 'Total loan amount'].includes(title)
    ) {
        return `$${value}`;
    }

    return value || '-';
}

export default function ProspectQuoteFromResponse({ prospectId, matterType }: Props) {
    const theme = useTheme();
    const displayHeader = getContactInfoHeader(matterType);

    const {
        data: quoteResp = [],
        isPending,
        isError,
        isFetching,
        error,
        refetch,
    } = useQuery<QuoteFormField[]>({
        queryKey: ['prospect-quote-form-response', prospectId],
        queryFn: async () => {
            if (!prospectId) return [];
            const quoteFormDataResponse = await getQuoteFormResponse(prospectId);
            const parsed = JSON.parse(quoteFormDataResponse.data.formResponse) as QuoteFormField[];
            return resolveUploadFields(parsed);
        },
        enabled: !!prospectId,
        staleTime: 5 * 60_000,
    });

    if (isPending) {
        return <ProspectQuoteFromResponseSkeleton />;
    }

    if (isError && isAxiosNotFoundError(error)) {
        return <ProspectDetailsEmptyPage message="Quote response not received from client yet" />;
    }

    if (isError) {
        return (
            <ProspectDetailsErrorPage
                message="There was an error fetching the quote form response"
                onRetry={refetch}
                isRetrying={isFetching}
            />
        );
    }

    const modifiedQuoteResp = quoteResp.filter(
        (field) =>
            field.Name === 'firstName' ||
            field.Name === 'middleName' ||
            field.Name === 'lastName' ||
            field.Name === 'email' ||
            field.Name === 'primaryPhone',
    );

    const splitValues = modifiedQuoteResp.map((field) => ({
        ...field,
        Value: field?.Value ? field.Value.split('|') : [],
    }));

    const groupCount = splitValues[0]?.Value.length;

    const grouped =
        groupCount > 0
            ? Array.from({ length: groupCount }, (_, i) =>
                  splitValues.map((field) => ({
                      ...field,
                      Value: field?.Value[i],
                  })),
              )
            : [];

    return (
        <Box
            sx={{
                borderRadius: '8px',
                padding: '24px',
                margin: '24px',
                border: `1px solid ${theme.palette.neutral.borderColor}`,
                backgroundColor: 'neutral.white',
                display: 'flex',
                flexDirection: 'column',
                gap: '18px',
                height: 'fit-content',
            }}
        >
            <Typography sx={{ order: 0 }} variant="h2">
                Quote form responses
            </Typography>

            {quoteResp
                .filter((field) => field.Name === 'purchasersListedOnPurchaseAndSaleAgreement')
                .map((field) => {
                    const isUploadField = !!field._fileId;
                    const formattedValue = formatFieldValue(field.Title, field.Value);

                    return (
                        <EditableViewComponentLeftAligned
                            key={field.Title}
                            label={field.Title}
                            value={formattedValue}
                            isCopyable={!isUploadField}
                            link={isUploadField ? '#' : undefined}
                            onLinkClick={(e) => {
                                e.preventDefault();
                                handleDownload(
                                    `${import.meta.env.VITE_APP_API_URL}/api/v1/files/${field?._fileId}`,
                                    field?.Value,
                                );
                            }}
                        />
                    );
                })}
            {grouped.length > 0 ? (
                grouped.map((group, idx) => (
                    <Accordion
                        key={`accordion-${idx}`}
                        sx={{
                            width: '100%',
                            border: `1px solid ${theme.palette.neutral.N_300}`,
                            borderRadius: '4px !important',
                            boxShadow: 'none !important',
                            '&.MuiAccordion-root.Mui-expanded': {
                                boxShadow: '0px 4px 10px 0px #2F303233 !important',
                                border: '0px !important',
                            },

                            '&::before': {
                                content: 'none',
                            },
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant="h3">
                                {`${ordinalNumbers[idx]} ${displayHeader}`}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                {group.map((field) => {
                                    const isUploadField = !!field._fileId;
                                    const formattedValue = formatFieldValue(
                                        field.Title,
                                        field.Value,
                                    );

                                    return (
                                        <Grid item xs={12} sm={12} key={`${field.Title}-${idx}`}>
                                            <EditableViewComponentLeftAligned
                                                label={field.Title}
                                                value={formattedValue}
                                                isCopyable={!isUploadField}
                                                link={isUploadField ? '#' : undefined}
                                                onLinkClick={(e) => {
                                                    e.preventDefault();
                                                    handleDownload(
                                                        `${import.meta.env.VITE_APP_API_URL}/api/v1/files/${field?._fileId}`,
                                                        field?.Value,
                                                    );
                                                }}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                ))
            ) : (
                <Typography>No purchaser information available.</Typography>
            )}
            {quoteResp
                .filter(
                    (field) =>
                        field.Name !== 'purchasersListedOnPurchaseAndSaleAgreement' &&
                        field.Name !== 'firstName' &&
                        field.Name !== 'middleName' &&
                        field.Name !== 'lastName' &&
                        field.Name !== 'email' &&
                        field.Name !== 'primaryPhone' &&
                        field.Value,
                )
                .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
                .map((field) => {
                    const isUploadField = !!field._fileId;
                    let displayValue = field.Value;

                    if (dateStringRegex.test(field.Value)) {
                        displayValue = DateTime.fromJSDate(new Date(field.Value)).toFormat(
                            'MM/dd/yyyy',
                        );
                    }

                    const formattedValue = formatFieldValue(field.Title, displayValue);

                    return (
                        <EditableViewComponentLeftAligned
                            key={field.Title}
                            label={field.Title}
                            value={formattedValue}
                            isCopyable={!isUploadField}
                            link={isUploadField ? '#' : undefined}
                            onLinkClick={(e) => {
                                e.preventDefault();
                                handleDownload(
                                    `${import.meta.env.VITE_APP_API_URL}/api/v1/files/${field?._fileId}`,
                                    field?.Value,
                                );
                            }}
                        />
                    );
                })}
        </Box>
    );
}
